import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import Pagination from '../components/ui/Pagination';
import { apiService } from '../services/api';
import { formatDate } from '../lib/utils';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { useToastNotifications } from '../components/ui/Toast';
import { 
  UserCheck, 
  Search, 
  Plus, 
  Edit, 
  UserX,
  Users as UsersIcon,
  Key,
  Activity,
  Trash2,
  X,
  Save,
  LogOut,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

interface CmsUser {
  _id: string;
  mysqlUserId?: number;
  name: string;
  username: string;
  email: string;
  role: string;
  status: string;
  system: string;
  createdAt: string;
  updatedAt: string;
  tokens?: {
    accessToken?: string;
    refreshToken?: string;
    apiKey?: string;
  };
  settings?: {
    theme?: string;
    language?: string;
    notifications?: boolean;
  };
  permissions?: {
    canCreateUsers?: boolean;
    canDeleteUsers?: boolean;
    canManagePackages?: boolean;
    canViewLogs?: boolean;
    canManageSettings?: boolean;
    canViewReports?: boolean;
    canManageResellers?: boolean;
    canCreateSubResellers?: boolean;
    canViewVod?: boolean;
    canDownload?: boolean;
    canChangeUsername?: boolean;
    canChangePassword?: boolean;
    canChangeBouquets?: boolean;
    canRestrict?: boolean;
    canViewClientLogs?: boolean;
    canManageCredits?: boolean;
    canViewStatistics?: boolean;
    canManageUsers?: boolean;
  };
  session?: {
    isOnline: boolean;
    lastLogin?: string;
  };
  security?: {
    twoFactorEnabled: boolean;
    loginAttempts: number;
    allowedIPs: string[];
  };
}

const Users: React.FC = () => {
  const { isAdmin, isSocio, isMaster } = useAuth();
  const { showSuccess, showError } = useToastNotifications();
  const [cmsUsers, setCmsUsers] = useState<CmsUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Estados para modais
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CmsUser | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    username: '',
    email: '',
    role: '',
    permissions: {} as CmsUser['permissions']
  });
  const [createFormData, setCreateFormData] = useState({
    userId: '',
    cmsPassword: '',
    selectedXtreamUser: null as any
  });
  const [xtreamUsers, setXtreamUsers] = useState<any[]>([]);
  const [loadingXtreamUsers, setLoadingXtreamUsers] = useState(false);
  const [searchXtreamTerm, setSearchXtreamTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Estados para limpeza de sessões
  const [showClearSessionsModal, setShowClearSessionsModal] = useState(false);
  const [clearingSessions, setClearingSessions] = useState(false);
  const [activeSessionsCount, setActiveSessionsCount] = useState<number>(0);
  const [sessionsEndpointAvailable, setSessionsEndpointAvailable] = useState<boolean>(true);
  
  // Debounce do termo de busca para otimizar performance
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    online: 0,
    withApiKey: 0,
    byRole: {} as Record<string, { total: number; active: number; inactive: number; color: string }>
  });

  // Memoizar isAdmin para evitar recriações desnecessárias


  const fetchCmsUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsSearching(true);
      const options = {
        page: currentPage,
        per: 15,
        search: debouncedSearchTerm || undefined
      };
      
      const response = await apiService.getCmsUsers(options);

      if (response && response.data) {
        setCmsUsers(Array.isArray(response.data) ? response.data : []);
        setTotalPages(response.total_pages || 1);
        setTotalUsers(response.total || 0);
      } else {
        setCmsUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
      }
    } catch (error: any) {
      setCmsUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, debouncedSearchTerm]);

  const fetchCmsStats = useCallback(async () => {
    try {
      const statsResponse = await apiService.getCmsUsersStats();
      
      // Garantir que byRole sempre seja um objeto
      const safeStats = {
        total: (statsResponse && statsResponse.total) || 0,
        active: (statsResponse && statsResponse.active) || 0,
        inactive: (statsResponse && statsResponse.inactive) || 0,
        online: (statsResponse && statsResponse.online) || 0,
        withApiKey: (statsResponse && statsResponse.withApiKey) || 0,
        byRole: (statsResponse && statsResponse.byRole) || {}
      };
      
      setStats(safeStats);
    } catch (error: any) {
      // Erro ao buscar estatísticas CMS
    }
  }, []);

  // Função para buscar contagem de sessões ativas
  const fetchActiveSessionsCount = useCallback(async () => {
    try {
      const data = await apiService.getActiveSessions();
      setActiveSessionsCount(data.count || 0);
      setSessionsEndpointAvailable(true);
    } catch (error: any) {
      console.error('Erro ao buscar contagem de sessões ativas:', error);
      
      // Se der erro de timeout ou 404, marcar endpoint como indisponível
      if (error.code === 'ECONNABORTED' || error.response?.status === 404) {
        setSessionsEndpointAvailable(false);
      }
      
      // Usar a contagem de usuários online como fallback
      setActiveSessionsCount(stats.online || 0);
    }
  }, [stats.online]);

  // Função para limpar todas as sessões
  const handleClearAllSessions = async () => {
    try {
      setClearingSessions(true);
      await apiService.clearAllUserSessions();
      setShowClearSessionsModal(false);
      setActiveSessionsCount(0);
      
      // Atualizar estatísticas após limpeza
      fetchCmsStats();
      
      showSuccess(
        'Sessões Limpas',
        'Todas as sessões foram limpas com sucesso! Todos os usuários não-admin precisarão fazer login novamente.'
      );
    } catch (error: any) {
      console.error('Erro ao limpar sessões:', error);
      
      // Verificar se é erro de timeout ou endpoint não encontrado
      if (error.code === 'ECONNABORTED' || error.response?.status === 404) {
        showError(
          'Endpoint Indisponível',
          'Endpoint de limpeza de sessões não está disponível no backend'
        );
      } else {
        showError('Erro ao Limpar Sessões', 'Não foi possível limpar as sessões');
      }
    } finally {
      setClearingSessions(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    fetchCmsUsers();
    fetchCmsStats();
  }, [currentPage, debouncedSearchTerm, fetchCmsUsers, fetchCmsStats]);

  // Carregar contagem de sessões ativas apenas para admins
  useEffect(() => {
    if (isAdmin()) {
      fetchActiveSessionsCount();
    } else {
      // Se não for admin, usar contagem de usuários online
      setActiveSessionsCount(stats.online || 0);
    }
  }, [isAdmin, stats.online, fetchActiveSessionsCount]);

  // Reset página quando termo de busca mudar
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // A busca será executada automaticamente pelo debounce
    // Apenas resetamos a página se necessário
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };


  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await apiService.updateCmsUserStatus(userId, newStatus);
      
      showSuccess(
        'Status Atualizado',
        `Usuário ${newStatus === 'active' ? 'ativado' : 'desativado'} com sucesso!`
      );
      
      fetchCmsUsers(); // Recarregar lista
      fetchCmsStats(); // Recarregar estatísticas
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      showError('Erro ao Atualizar Status', 'Não foi possível alterar o status do usuário');
    }
  };

  const handleGenerateApiKey = async (userId: string) => {
    try {
      await apiService.generateCmsUserApiKey(userId);
      
      showSuccess(
        'API Key Gerada',
        'Nova API key foi gerada com sucesso!'
      );
      
      fetchCmsUsers(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao gerar API key:', error);
      showError('Erro ao Gerar API Key', 'Não foi possível gerar nova API key');
    }
  };

  // Função para abrir modal de edição
  const handleEditUser = (user: CmsUser) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      username: user.username || '',
      email: user.email || '',
      role: user.role || '',
      permissions: user.permissions || {}
    });
    setShowEditModal(true);
  };

  // Função para abrir modal de confirmação de remoção
  const handleDeleteUser = (user: CmsUser) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Função para salvar edição
  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    
    setIsSaving(true);
    try {
      await apiService.updateCmsUser(selectedUser._id, editFormData);
      setShowEditModal(false);
      fetchCmsUsers(); // Recarregar lista
      fetchCmsStats(); // Recarregar estatísticas
      
      showSuccess(
        'Usuário Atualizado',
        `As alterações do usuário "${selectedUser.username}" foram salvas com sucesso!`
      );
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      
      // Mostrar mensagem de erro mais amigável
      if (error.response?.data?.error === 'Permissões inválidas') {
        console.error('❌ Permissões inválidas:', error.response.data.invalidPermissions);
        showError('Permissões Inválidas', 'Algumas permissões fornecidas não são válidas');
      } else if (error.response?.data?.error === 'Erro de validação') {
        console.error('❌ Erro de validação:', error.response.data.details);
        showError('Erro de Validação', 'Verifique os dados inseridos');
      } else {
        showError('Erro ao Atualizar', 'Não foi possível atualizar o usuário');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Função para confirmar remoção
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    
    setIsDeleting(true);
    try {
      await apiService.deleteCmsUser(selectedUser._id);
      setShowDeleteModal(false);
      
      showSuccess(
        'Usuário Removido',
        `Usuário "${selectedUser.username}" foi removido com sucesso!`
      );
      
      fetchCmsUsers(); // Recarregar lista
      fetchCmsStats(); // Recarregar estatísticas
    } catch (error) {
      console.error('Erro ao remover usuário:', error);
      showError('Erro ao Remover', 'Não foi possível remover o usuário');
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para fechar modais
  const handleCloseModals = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowCreateModal(false);
    setSelectedUser(null);
    setCreateFormData({
      userId: '',
      cmsPassword: '',
      selectedXtreamUser: null
    });
    setXtreamUsers([]);
    setSearchXtreamTerm('');
  };

  // Buscar revendedores do Xtream para sincronizar
  // NOTA: Esta função já respeita a hierarquia automaticamente via backend
  // - Admins: veem todos os revendedores
  // - Masters/Sócios: veem apenas revendedores da sua árvore (owner_id)
  const searchXtreamUsers = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setXtreamUsers([]);
      return;
    }

    setLoadingXtreamUsers(true);
    try {
      // Buscar revendedores (Resellers) do Xtream que contenham o termo
      // O backend já filtra por owner_id para não-admins
      const response = await apiService.getResellers({
        search: term,
        page: 1,
        per: 20
      });

      // Filtrar apenas os que não têm CMS
      const usersWithoutCms: any[] = [];
      
      for (const reseller of response.data || []) {
        // FILTRO: Não-admins não podem ver usuários do grupo admin (member_group_id = 1)
        if (!isAdmin() && reseller.member_group_id === 1) {
          continue; // Pular usuários admin
        }

        try {
          // Tentar buscar se já existe no CMS pelo MySQL ID
          await apiService.getCmsUserByMysqlId(reseller.id);
          // Se chegou aqui, já existe no CMS, não adicionar
        } catch (error) {
          // Se der erro 404, significa que não existe no CMS
          usersWithoutCms.push(reseller);
        }
      }

      setXtreamUsers(usersWithoutCms);
    } catch (error) {
      console.error('Erro ao buscar revendedores Xtream:', error);
      setXtreamUsers([]);
    } finally {
      setLoadingXtreamUsers(false);
    }
  }, [isAdmin]);

  // Debounce da busca de usuários Xtream
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchXtreamTerm) {
        searchXtreamUsers(searchXtreamTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchXtreamTerm, searchXtreamUsers]);

  // Selecionar usuário Xtream
  const handleSelectXtreamUser = (user: any) => {
    setCreateFormData({
      userId: user.id.toString(),
      cmsPassword: '',
      selectedXtreamUser: user
    });
    setSearchXtreamTerm('');
    setXtreamUsers([]);
  };

  // Função para sincronizar usuário do Xtream para CMS
  // NOTA: O backend já valida se o usuário pertence à árvore do revendedor
  const handleSyncUser = async () => {
    if (!createFormData.userId || !createFormData.cmsPassword) {
      showError('Dados Incompletos', 'Selecione um usuário e defina uma senha para o CMS');
      return;
    }

    setIsCreating(true);
    try {
      // Chamada à API para sincronizar o usuário
      await apiService.syncUserToCms(
        parseInt(createFormData.userId), 
        createFormData.cmsPassword
      );
      
      setShowCreateModal(false);
      handleCloseModals();
      
      fetchCmsUsers(); // Recarregar lista
      fetchCmsStats(); // Recarregar estatísticas
      
      showSuccess(
        'Usuário Sincronizado',
        `Usuário "${createFormData.selectedXtreamUser?.username}" foi sincronizado com sucesso para o CMS!`
      );
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro ao sincronizar usuário';
      
      // Se for erro de permissão, mostrar mensagem específica
      if (error.response?.status === 403) {
        showError(
          'Acesso Negado',
          'Você só pode sincronizar usuários da sua árvore de revendedores'
        );
      } else {
        showError('Erro na Sincronização', errorMessage);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'reseller': return 'bg-blue-100 text-blue-800';
      case 'user': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {

    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando usuários CMS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usuários CMS</h1>
          <p className="text-gray-600">
            {isAdmin() 
              ? 'Gerenciar todos os usuários do sistema CMS (MongoDB)'
              : 'Gerenciar usuários da sua árvore no sistema CMS'
            }
          </p>
        </div>
        {(isAdmin() || isSocio() || isMaster()) && (
          <Button 
            className="flex items-center gap-2"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={16} />
            Novo Usuário
          </Button>
        )}
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 text-blue-600">
                <UsersIcon size={32} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 text-green-600">
                <UserCheck size={32} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 text-green-600">
                <Activity size={32} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Online</p>
                <p className="text-2xl font-bold text-gray-900">{stats.online}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 text-purple-600">
                <Key size={32} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Com API Key</p>
                <p className="text-2xl font-bold text-gray-900">{stats.withApiKey}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Botão de Limpeza de Sessões - Apenas para Admins */}
      {isAdmin() && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                  <LogOut className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-900">Gerenciamento de Sessões</h3>
                  <p className="text-sm text-red-700">
                    Atualmente há <strong>{activeSessionsCount}</strong> usuários não-admin ativos no sistema
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Apenas usuários não-admin serão desconectados. Administradores permanecerão logados.
                  </p>
                </div>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowClearSessionsModal(true)}
                disabled={clearingSessions || activeSessionsCount === 0 || !sessionsEndpointAvailable}
                className="flex items-center gap-2"
              >
                {clearingSessions ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Limpando...
                  </>
                ) : !sessionsEndpointAvailable ? (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    Endpoint Indisponível
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4" />
                    Limpar Todas as Sessões
                  </>
                )}
              </Button>
            </div>
            {!sessionsEndpointAvailable && (
              <p className="text-xs text-orange-600 mt-2">
                ⚠️ Endpoint de sessões não está disponível no backend. Usando contagem de usuários online como referência.
              </p>
            )}
            {activeSessionsCount === 0 && sessionsEndpointAvailable && (
              <p className="text-xs text-red-600 mt-2">
                Não há sessões ativas para limpar.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Estatísticas por Role */}
      {stats.byRole && Object.keys(stats.byRole).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Usuários por Função</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.byRole && Object.entries(stats.byRole).map(([role, roleStats]) => (
                <div key={role} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium capitalize">{role}</h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{roleStats.total}</div>
                      <div className="text-sm text-gray-500">
                        {roleStats.active} ativos, {roleStats.inactive} inativos
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros e Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Input
                  placeholder="Buscar por nome, username, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={isSearching ? "pr-10" : ""}
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
            </div>
            <Button type="submit" variant="outline" className="flex items-center gap-2">
              <Search size={16} />
              {isSearching ? 'Buscando...' : 'Buscar'}
            </Button>
          </form>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-500">
              Busca automática ativada. Resultados serão atualizados automaticamente.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Usuários CMS */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários CMS</CardTitle>
          <CardDescription>
            {totalUsers} usuários encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4">Usuário</th>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Função</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Sistema</th>
                  <th className="text-left p-4">API Key</th>
                  <th className="text-left p-4">Online</th>
                  <th className="text-left p-4">Último Login</th>
                  <th className="text-left p-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {cmsUsers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <UsersIcon size={48} className="text-gray-300 mb-4" />
                        <p className="text-lg font-medium">Nenhum usuário CMS encontrado</p>
                        <p className="text-sm">Os usuários CMS aparecerão aqui quando forem criados</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  cmsUsers.map((cmsUser) => (
                    <tr key={cmsUser._id} className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{cmsUser.name}</p>
                        <p className="text-sm text-gray-600">@{cmsUser.username}</p>
                        {cmsUser.mysqlUserId && (
                          <p className="text-xs text-gray-500">MySQL ID: {cmsUser.mysqlUserId}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-4">{cmsUser.email}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(cmsUser.role)}`}>
                        {cmsUser.role}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(cmsUser.status)}`}>
                        {cmsUser.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {cmsUser.system}
                      </span>
                    </td>
                    <td className="p-4">
                      {cmsUser.tokens?.apiKey ? (
                        <span className="text-green-600 text-sm">✓ Ativa</span>
                      ) : (
                        <span className="text-gray-400 text-sm">✗ Nenhuma</span>
                      )}
                    </td>
                    <td className="p-4">
                      {cmsUser.session?.isOnline ? (
                        <span className="text-green-600 text-sm">🟢 Online</span>
                      ) : (
                        <span className="text-gray-400 text-sm">⚫ Offline</span>
                      )}
                    </td>
                    <td className="p-4">
                      {cmsUser.session?.lastLogin ? formatDate(cmsUser.session.lastLogin) : 'Nunca'}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {(isAdmin() || isSocio() || isMaster()) && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditUser(cmsUser)}
                            title="Editar usuário"
                          >
                            <Edit size={16} />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleGenerateApiKey(cmsUser._id)}
                          title="Gerar API Key"
                        >
                          <Key size={16} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant={cmsUser.status === 'active' ? 'destructive' : 'default'}
                          onClick={() => handleToggleUserStatus(cmsUser._id, cmsUser.status)}
                          title={cmsUser.status === 'active' ? 'Desativar usuário' : 'Ativar usuário'}
                        >
                          {cmsUser.status === 'active' ? (
                            <UserX size={16} />
                          ) : (
                            <UserCheck size={16} />
                          )}
                        </Button>
                        {isAdmin() && (
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDeleteUser(cmsUser)}
                            title="Remover usuário"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalUsers}
            itemsPerPage={15}
          />
        </CardContent>
      </Card>

      {/* Modal de Edição de Usuário */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar Usuário CMS</h2>
              <Button variant="outline" size="sm" onClick={handleCloseModals}>
                <X size={16} />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Informações Básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome *</label>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Username *</label>
                  <Input
                    value={editFormData.username}
                    onChange={(e) => setEditFormData({...editFormData, username: e.target.value})}
                    placeholder="Nome de usuário"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Função *</label>
                  <select 
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {isAdmin() && <option value="admin">Administrador</option>}
                    <option value="reseller">Revendedor</option>
                    <option value="user">Usuário</option>
                  </select>
                  {!isAdmin() && (
                    <p className="text-xs text-gray-500 mt-1">
                      Apenas administradores podem criar usuários com função "Administrador"
                    </p>
                  )}
                </div>
              </div>

              {/* Permissões */}
              <div>
                <label className="block text-sm font-medium mb-3">Permissões do Sistema</label>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Nota:</strong> As permissões "Criar/Deletar Usuários CMS" referem-se aos usuários do sistema CMS (MongoDB), 
                  não às linhas de clientes IPTV. Para gerenciar clientes IPTV, use a seção "Clientes".
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'canCreateUsers', label: 'Criar Usuários CMS' },
                    { key: 'canDeleteUsers', label: 'Deletar Usuários CMS' },
                    { key: 'canManagePackages', label: 'Gerenciar Pacotes IPTV' },
                    { key: 'canViewLogs', label: 'Ver Logs do Sistema' },
                    { key: 'canManageSettings', label: 'Gerenciar Configurações' },
                    { key: 'canViewReports', label: 'Ver Relatórios' },
                    { key: 'canManageResellers', label: 'Gerenciar Revendedores' },
                    { key: 'canCreateSubResellers', label: 'Criar Sub-revendedores' },
                    { key: 'canViewVod', label: 'Ver VOD' },
                    { key: 'canDownload', label: 'Downloads' },
                    { key: 'canChangeUsername', label: 'Alterar Username' },
                    { key: 'canChangePassword', label: 'Alterar Senha' },
                    { key: 'canChangeBouquets', label: 'Alterar Bouquets' },
                    { key: 'canRestrict', label: 'Aplicar Restrições' },
                    { key: 'canViewClientLogs', label: 'Ver Logs de Cliente' },
                    { key: 'canManageCredits', label: 'Gerenciar Créditos' },
                    { key: 'canViewStatistics', label: 'Ver Estatísticas' },
                    { key: 'canManageUsers', label: 'Gerenciar Usuários CMS' }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editFormData.permissions?.[key as keyof typeof editFormData.permissions] || false}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          permissions: {
                            ...editFormData.permissions,
                            [key]: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={handleCloseModals}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="flex items-center gap-2"
              >
                <Save size={16} />
                {isSaving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Remoção */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Confirmar Remoção</h2>
                <p className="text-sm text-gray-600">Esta ação não pode ser desfeita</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Tem certeza que deseja remover o usuário <strong>{selectedUser.name}</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Todos os dados do usuário CMS serão permanentemente removidos.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCloseModals}>
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex items-center gap-2"
              >
                <Trash2 size={16} />
                {isDeleting ? 'Removendo...' : 'Remover'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sincronização de Usuário */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Sincronizar Usuário do Xtream para CMS</h2>
              <Button variant="outline" size="sm" onClick={handleCloseModals}>
                <X size={16} />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Como funciona:</strong> Busque um usuário que já existe no sistema Xtream IPTV 
                  e sincronize-o com o CMS. Os dados do usuário serão migrados automaticamente.
                </p>
                {!isAdmin() && (
                  <p className="text-sm text-blue-700 mt-2">
                    <strong>Nota:</strong> Você só pode sincronizar usuários da sua árvore de revendedores.
                  </p>
                )}
              </div>

              {/* Busca de revendedor Xtream */}
              {!createFormData.selectedXtreamUser ? (
                <div>
                  <label className="block text-sm font-medium mb-2">Buscar Revendedor no Xtream IPTV *</label>
                  <div className="relative">
                    <Input
                      value={searchXtreamTerm}
                      onChange={(e) => setSearchXtreamTerm(e.target.value)}
                      placeholder="Digite o username ou email para buscar..."
                      className="pr-10"
                    />
                    {loadingXtreamUsers && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Digite pelo menos 2 caracteres para buscar revendedores</p>

                  {/* Lista de resultados */}
                  {xtreamUsers.length > 0 && (
                    <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                      {xtreamUsers.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => handleSelectXtreamUser(user)}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{user.username}</p>
                              <p className="text-sm text-gray-600">{user.email || 'Sem email'}</p>
                              <p className="text-xs text-gray-500">
                                Grupo: {user.member_group_id} | Status: {user.status === 1 ? 'Ativo' : 'Inativo'}
                              </p>
                            </div>
                            <Button size="sm" variant="outline">
                              Selecionar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {searchXtreamTerm.length >= 2 && !loadingXtreamUsers && xtreamUsers.length === 0 && (
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg text-center text-gray-600">
                      Nenhum revendedor encontrado ou todos já estão sincronizados
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Revendedor selecionado */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-green-900">Revendedor Selecionado</p>
                        <p className="font-bold text-lg mt-1">{createFormData.selectedXtreamUser.username}</p>
                        <p className="text-sm text-gray-700">{createFormData.selectedXtreamUser.email || 'Sem email'}</p>
                        <div className="mt-2 space-y-1 text-xs text-gray-600">
                          <p>• ID MySQL: {createFormData.selectedXtreamUser.id}</p>
                          <p>• Grupo: {createFormData.selectedXtreamUser.member_group_id}</p>
                          <p>• Status: {createFormData.selectedXtreamUser.status === 1 ? 'Ativo' : 'Inativo'}</p>
                          <p>• Créditos: {createFormData.selectedXtreamUser.credits || 0}</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setCreateFormData({...createFormData, selectedXtreamUser: null, userId: ''})}
                      >
                        Trocar
                      </Button>
                    </div>
                  </div>

                  {/* Senha do CMS */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Senha de Acesso ao CMS *</label>
                    <Input
                      type="password"
                      value={createFormData.cmsPassword}
                      onChange={(e) => setCreateFormData({...createFormData, cmsPassword: e.target.value})}
                      placeholder="••••••"
                      minLength={6}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Defina a senha que este usuário usará para acessar o CMS (mínimo 6 caracteres)
                    </p>
                  </div>

                  {/* Informações sobre a sincronização */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-900 mb-2">O que será sincronizado:</p>
                    <ul className="text-xs text-yellow-700 space-y-1">
                      <li>✓ Username e email do Xtream</li>
                      <li>✓ Grupo e permissões baseados no member_group_id</li>
                      <li>✓ Status atual do usuário</li>
                      <li>✓ Créditos disponíveis</li>
                      <li>✓ Vínculo automático entre MySQL e MongoDB</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Resumo:</strong>
                    </p>
                    <ul className="text-xs text-gray-600 mt-2 space-y-1">
                      <li>→ Usuário já existe no MySQL (Xtream IPTV)</li>
                      <li>→ Conta CMS será criada no MongoDB</li>
                      <li>→ Vinculação automática entre os dois sistemas</li>
                      <li>→ Senha separada apenas para o CMS</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={handleCloseModals}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSyncUser}
                disabled={isCreating || !createFormData.selectedXtreamUser || !createFormData.cmsPassword || createFormData.cmsPassword.length < 6}
                className="flex items-center gap-2"
              >
                <RefreshCw size={16} />
                {isCreating ? 'Sincronizando...' : 'Sincronizar Usuário'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação para Limpeza de Sessões */}
      {showClearSessionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-semibold text-red-900">Confirmar Limpeza de Sessões</h3>
            </div>
            
            <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Tem certeza que deseja limpar as sessões ativas? Esta ação irá:
            </p>
            
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Desconectar <strong>{activeSessionsCount} usuários não-admin</strong> do sistema</li>
              <li>Administradores permanecerão logados</li>
              <li>Usuários afetados precisarão fazer login novamente</li>
              <li>Esta ação não pode ser desfeita</li>
            </ul>
              
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Aviso:</strong> Apenas usuários não-admin serão desconectados. Administradores permanecerão logados.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowClearSessionsModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleClearAllSessions}
                disabled={clearingSessions}
                className="flex-1"
              >
                {clearingSessions ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Limpando...
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4 mr-2" />
                    Confirmar Limpeza
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;